import React, { useState } from 'react';
import { Specialization, CATEGORY_COLORS } from '../types';

interface SpecializationSelectorProps {
  specializations: Specialization[];
  selectedSpecs: string[];
  chosenSpec?: string;
  onChange: (selectedSpecs: string[], chosenSpec?: string) => void;
  level: number;
}

export const SpecializationSelector: React.FC<SpecializationSelectorProps> = ({
  specializations,
  selectedSpecs,
  chosenSpec,
  onChange,
  level
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleSpecToggle = (specName: string) => {
    let newSelectedSpecs: string[];
    
    if (selectedSpecs.includes(specName)) {
      // Remove from selection
      newSelectedSpecs = selectedSpecs.filter(s => s !== specName);
    } else if (selectedSpecs.length < 4) {
      // Add to selection (max 4)
      newSelectedSpecs = [...selectedSpecs, specName];
    } else {
      // Replace the last one if already at max
      newSelectedSpecs = [...selectedSpecs.slice(0, 3), specName];
    }
    
    // If the chosen spec is no longer in selected specs, clear it
    const newChosenSpec = newSelectedSpecs.includes(chosenSpec || '') ? chosenSpec : undefined;
    
    onChange(newSelectedSpecs, newChosenSpec);
  };

  const handleChosenSpecChange = (specName: string) => {
    if (selectedSpecs.includes(specName)) {
      onChange(selectedSpecs, chosenSpec === specName ? undefined : specName);
    }
  };

  const getSpecColor = (spec: Specialization) => {
    return CATEGORY_COLORS[spec.general_category];
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-2 border rounded-md text-left bg-white shadow-sm hover:bg-gray-50 min-h-[80px]"
      >
        <div className="text-xs text-gray-500 mb-1">Level {level}</div>
        {selectedSpecs.length === 0 ? (
          <div className="text-gray-400 text-sm">Select 4 specs...</div>
        ) : (
          <div className="space-y-1">
            {selectedSpecs.map((specName, index) => {
              const spec = specializations.find(s => s.name === specName);
              if (!spec) return null;
              
              return (
                <div
                  key={specName}
                  className={`text-xs p-1 rounded flex items-center justify-between ${
                    chosenSpec === specName ? 'ring-2 ring-gray-800' : ''
                  }`}
                  style={{ 
                    backgroundColor: `${getSpecColor(spec)}20`,
                    borderLeft: `3px solid ${getSpecColor(spec)}`
                  }}
                >
                  <span className="truncate">{spec.name}</span>
                  {chosenSpec === specName && (
                    <span className="text-xs font-bold ml-1">✓</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </button>

      {isOpen && (
        <div className="absolute z-10 mt-1 w-96 bg-white border rounded-md shadow-lg max-h-96 overflow-y-auto">
          <div className="p-3">
            <div className="text-sm font-medium mb-2">
              Select 4 specializations offered for Level {level}
            </div>
            <div className="text-xs text-gray-500 mb-3">
              {selectedSpecs.length}/4 selected
            </div>
            
            {Object.entries(
              specializations.reduce((acc, spec) => {
                if (!acc[spec.general_category]) acc[spec.general_category] = [];
                acc[spec.general_category].push(spec);
                return acc;
              }, {} as { [key: string]: Specialization[] })
            ).map(([category, specs]) => (
              <div key={category} className="mb-4">
                <div 
                  className="text-sm font-medium mb-2 px-2 py-1 rounded"
                  style={{ 
                    backgroundColor: `${CATEGORY_COLORS[category as keyof typeof CATEGORY_COLORS]}20`,
                    color: CATEGORY_COLORS[category as keyof typeof CATEGORY_COLORS]
                  }}
                >
                  {category} ({specs.length})
                </div>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {specs.map((spec) => (
                    <div
                      key={spec.name}
                      className={`p-2 rounded cursor-pointer text-sm ${
                        selectedSpecs.includes(spec.name)
                          ? 'bg-blue-100 border-blue-300'
                          : 'hover:bg-gray-100'
                      }`}
                      onClick={() => handleSpecToggle(spec.name)}
                    >
                      <div className="font-medium">{spec.name}</div>
                      <div className="text-xs text-gray-600 truncate">
                        {spec.description.slice(0, 80)}...
                      </div>
                      {selectedSpecs.includes(spec.name) && (
                        <div className="mt-1">
                          <label className="flex items-center text-xs">
                            <input
                              type="radio"
                              name={`chosen-${level}`}
                              checked={chosenSpec === spec.name}
                              onChange={() => handleChosenSpecChange(spec.name)}
                              className="mr-1"
                            />
                            Mark as chosen
                          </label>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          
          <div className="border-t p-3 bg-gray-50 flex justify-end">
            <button
              onClick={() => setIsOpen(false)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};